import { useEffect, useMemo } from 'react';
import { Alert, Button, Form, Input, InputNumber, Select, Space, Switch } from 'antd';
import type {
  CatalogCategoryBehavior,
  CreateCatalogCategoryDto,
  UpdateCatalogCategoryDto,
} from '@aid/shared';
import { useCategoriasList } from '../api/useCatalogosAdmin';

interface CatalogCategoryFormProps {
  mode: 'create' | 'edit';
  initialValues?: {
    code?: string;
    name?: string;
    description?: string | null;
    parentCategoryCode?: string | null;
    behavior?: CatalogCategoryBehavior;
    active?: boolean;
    systemManaged?: boolean;
  } | null;
  onSubmit: (values: CreateCatalogCategoryDto | UpdateCatalogCategoryDto) => void;
  loading?: boolean;
}

const defaultBehavior: CatalogCategoryBehavior = {
  version: 1,
  kind: 'standard',
  visibleInHub: true,
  hierarchy: { enabled: false, display: 'table' },
  lifecycle: { allowDeactivate: true, allowHardDelete: true },
  selection: { activeOnly: true },
  specialBehavior: 'none',
};

export function CatalogCategoryForm({
  mode,
  initialValues,
  onSubmit,
  loading,
}: CatalogCategoryFormProps) {
  const [form] = Form.useForm();
  const { data: categories, isError: categoriesError } = useCategoriasList();
  const hierarchyEnabled = Form.useWatch<boolean>('hierarchyEnabled', form) ?? false;
  const behavior = initialValues?.behavior ?? defaultBehavior;
  const systemManaged = initialValues?.systemManaged === true;

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        code: initialValues.code,
        name: initialValues.name,
        description: initialValues.description ?? '',
        parentCategoryCode: initialValues.parentCategoryCode ?? null,
        hierarchyEnabled: behavior.hierarchy.enabled,
        hierarchyDisplay: behavior.hierarchy.display,
        maxDepth: behavior.hierarchy.maxDepth,
        visibleInHub: behavior.visibleInHub,
        allowDeactivate: behavior.lifecycle.allowDeactivate,
        specialBehavior: behavior.specialBehavior,
        active: initialValues.active,
      });
    }
  }, [behavior, form, initialValues]);

  const parentCategoryOptions = useMemo(
    () =>
      (categories ?? [])
        .filter((item) => item.active && item.code !== initialValues?.code)
        .map((item) => ({ label: item.name, value: item.code })),
    [categories, initialValues?.code],
  );

  const handleFinish = (values: Record<string, unknown>) => {
    const hierarchyEnabledValue = values.hierarchyEnabled === true;
    const specialBehavior =
      values.specialBehavior === 'system-flag' || values.specialBehavior === 'status'
        ? values.specialBehavior
        : 'none';
    const nextBehavior: CatalogCategoryBehavior = {
      version: 1,
      kind:
        specialBehavior !== 'none' ? 'system' : hierarchyEnabledValue ? 'hierarchical' : 'standard',
      visibleInHub: values.visibleInHub !== false,
      hierarchy: {
        enabled: hierarchyEnabledValue,
        display: values.hierarchyDisplay === 'tree' ? 'tree' : 'table',
        ...(typeof values.maxDepth === 'number' ? { maxDepth: values.maxDepth } : {}),
      },
      lifecycle: {
        allowDeactivate: values.allowDeactivate !== false,
        allowHardDelete: true,
      },
      selection: { activeOnly: true },
      specialBehavior,
    };

    if (mode === 'create') {
      onSubmit({
        code: String(values.code ?? '')
          .trim()
          .toUpperCase(),
        name: String(values.name ?? '').trim(),
        description: values.description ? String(values.description).trim() : null,
        parentCategoryCode: values.parentCategoryCode ? String(values.parentCategoryCode) : null,
        behavior: nextBehavior,
        active: values.active !== false,
      });
      return;
    }

    if (systemManaged) {
      onSubmit({
        name: String(values.name ?? '').trim(),
        description: values.description ? String(values.description).trim() : null,
        active: values.active !== false,
      });
      return;
    }

    onSubmit({
      name: String(values.name ?? '').trim(),
      description: values.description ? String(values.description).trim() : null,
      parentCategoryCode: values.parentCategoryCode ? String(values.parentCategoryCode) : null,
      behavior: nextBehavior,
      active: values.active !== false,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      style={{ maxWidth: 720, width: '100%' }}
      initialValues={{
        ...initialValues,
        description: initialValues?.description ?? '',
        hierarchyEnabled: behavior.hierarchy.enabled,
        hierarchyDisplay: behavior.hierarchy.display,
        maxDepth: behavior.hierarchy.maxDepth,
        visibleInHub: behavior.visibleInHub,
        allowDeactivate: behavior.lifecycle.allowDeactivate,
        specialBehavior: behavior.specialBehavior,
        active: initialValues?.active ?? true,
      }}
    >
      {categoriesError && (
        <Alert
          type="warning"
          showIcon
          message="No se pudo cargar el catálogo de categorías"
          description="Podés crear la definición, pero verificá las relaciones antes de guardarla."
          style={{ marginBottom: 16 }}
        />
      )}
      {mode === 'create' && (
        <Form.Item
          name="code"
          label="Código"
          extra="Usá mayúsculas, números y guiones bajos. Este código será estable."
          rules={[{ required: true, message: 'El código es obligatorio' }]}
        >
          <Input placeholder="EJEMPLO_CATEGORIA" style={{ textTransform: 'uppercase' }} />
        </Form.Item>
      )}
      <Form.Item
        name="name"
        label="Nombre visible"
        rules={[{ required: true, message: 'El nombre es obligatorio' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="description" label="Descripción">
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item name="parentCategoryCode" label="Categoría padre">
        <Select
          allowClear
          disabled={systemManaged}
          placeholder="Sin categoría padre"
          options={parentCategoryOptions}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item name="hierarchyEnabled" label="Admite jerarquía" valuePropName="checked">
        <Switch disabled={systemManaged} checkedChildren="Sí" unCheckedChildren="No" />
      </Form.Item>
      {hierarchyEnabled && (
        <Space wrap size={[12, 8]} style={{ width: '100%' }}>
          <Form.Item name="hierarchyDisplay" label="Visualización" style={{ minWidth: 180 }}>
            <Select
              disabled={systemManaged}
              options={[
                { label: 'Tabla', value: 'table' },
                { label: 'Árbol', value: 'tree' },
              ]}
            />
          </Form.Item>
          <Form.Item name="maxDepth" label="Profundidad máxima">
            <InputNumber disabled={systemManaged} min={1} max={10} />
          </Form.Item>
        </Space>
      )}
      <Form.Item name="specialBehavior" label="Comportamiento especial">
        <Select
          disabled={systemManaged}
          options={[
            { label: 'Estándar', value: 'none' },
            { label: 'Estado del sistema', value: 'status' },
            { label: 'Indicador del sistema', value: 'system-flag' },
          ]}
        />
      </Form.Item>
      <Space wrap size={[16, 8]}>
        <Form.Item name="visibleInHub" label="Visible en el hub" valuePropName="checked">
          <Switch disabled={systemManaged} checkedChildren="Sí" unCheckedChildren="No" />
        </Form.Item>
        <Form.Item name="allowDeactivate" label="Permitir desactivar" valuePropName="checked">
          <Switch disabled={systemManaged} checkedChildren="Sí" unCheckedChildren="No" />
        </Form.Item>
        {mode === 'edit' && (
          <Form.Item name="active" label="Categoría activa" valuePropName="checked">
            <Switch disabled={systemManaged} checkedChildren="Sí" unCheckedChildren="No" />
          </Form.Item>
        )}
      </Space>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {mode === 'create' ? 'Crear categoría' : 'Guardar cambios'}
        </Button>
      </Form.Item>
    </Form>
  );
}
